/**
 * Analytics Service
 *
 * Calculates and aggregates metrics for dashboards from Supabase database
 * Provides both platform-wide and client-specific analytics
 */

import {
  computeFileMetricsByPeriod,
  isPeriodComplete,
  type FileGift,
} from "@/lib/donors/fileMetrics";
import { supabase } from "@/lib/supabaseClient";
import { getDonorDisplayName } from "@/utils/donorDisplay";

/**
 * Platform-wide dashboard metrics (all clients)
 */
export interface PlatformMetrics {
  totalClients: number;
  activeCampaigns: number;
  totalDonors: number;
  monthlyRevenue: number;
  avgGiftSize: number;
  donorsChange: number; // percentage change from previous period
  revenueChange: number; // percentage change from previous period
}

/**
 * Canonical phrase for the insufficient-history state. Single source of wording
 * so PDF, AI narrative, and Ask Nexus never invent slightly different copy.
 * Factual; does not blame the client.
 */
export const RETENTION_UNAVAILABLE_LABEL =
  "Insufficient giving history to calculate annual retention.";

/** A donation row reduced to the fields canonical retention needs. */
interface RetentionDonationRow {
  donor_id: string;
  date: string;
  amount_cents: number;
}

/**
 * Annual cohort retention for the latest COMPLETE calendar year (Jan-FY),
 * routed through the canonical file-metrics layer. Pure + deterministic.
 * Returns `available: false` (rate 0) when there is no prior-year denominator
 * — insufficient history is NOT 0% retention.
 */
export function computeAnnualRetention(
  rows: RetentionDonationRow[],
  today: Date = new Date(),
): { rate: number; available: boolean } {
  const gifts: FileGift[] = rows.map((r) => ({
    donorId: r.donor_id,
    amountCents: r.amount_cents,
    date: r.date,
  }));
  // Latest complete calendar year: currentYear is complete only on/after Dec 31.
  const currentYear = today.getUTCFullYear();
  const P = isPeriodComplete(currentYear, 1, today)
    ? currentYear
    : currentYear - 1;
  const metrics = computeFileMetricsByPeriod(
    gifts,
    [P - 1, P],
    { fiscalYearStartMonth: 1 },
    today,
  );
  const r = metrics[P]?.retentionRate;
  return r == null
    ? { rate: 0, available: false }
    : { rate: r * 100, available: true };
}

/**
 * Client-specific dashboard metrics
 */
export interface ClientMetrics {
  totalRaised: number;
  activeCampaigns: number;
  totalDonors: number;
  conversionRate: number;
  avgDonationSize: number;
  recentRepeatActivityRate: number; // last 30d vs prior 30d repeat activity — NOT annual retention
  recentDonationCount: number; // last 30 days
  donationGrowth: number; // percentage change
}

/**
 * Recent activity item
 */
export interface ActivityItem {
  id: string;
  type: "donation" | "campaign" | "email" | "segment";
  message: string;
  time: string;
  clientId: string;
  clientName?: string;
  entityId?: string;
  metadata?: Record<string, any>;
}

/**
 * Fetch platform-wide metrics for the main dashboard
 */
export async function getPlatformMetrics(): Promise<PlatformMetrics> {
  try {
    // Get total clients count
    const { count: totalClients, error: clientsError } = await supabase
      .from("clients")
      .select("*", { count: "exact", head: true })
      .eq("is_active", true)
      .is("deleted_at", null);

    if (clientsError) throw clientsError;

    // Get active campaigns count
    const { count: activeCampaigns, error: campaignsError } = await supabase
      .from("campaigns")
      .select("*", { count: "exact", head: true })
      .eq("status", "active")
      .is("deleted_at", null);

    if (campaignsError) throw campaignsError;

    // Get total donors count
    const { count: totalDonors, error: donorsError } = await supabase
      .from("donors")
      .select("*", { count: "exact", head: true })
      .eq("status", "active")
      .is("deleted_at", null);

    if (donorsError) throw donorsError;

    // Get monthly revenue (last 30 days)
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const { data: recentDonations, error: revenueError } = await supabase
      .from("donations")
      .select("amount_cents")
      .gte("date", thirtyDaysAgo.toISOString())
      .lte("date", new Date().toISOString()); // last 30 days THROUGH TODAY — exclude future-dated gifts

    if (revenueError) throw revenueError;

    const monthlyRevenue =
      recentDonations?.reduce(
        (sum, donation: any) => sum + (donation.amount_cents || 0),
        0,
      ) / 100 || 0;

    // Calculate average gift size
    const avgGiftSize =
      recentDonations && recentDonations.length > 0
        ? monthlyRevenue / recentDonations.length
        : 0;

    // Get previous period data for comparison
    const sixtyDaysAgo = new Date();
    sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60);

    const { data: previousDonations, error: prevError } = await supabase
      .from("donations")
      .select("amount_cents")
      .gte("date", sixtyDaysAgo.toISOString())
      .lt("date", thirtyDaysAgo.toISOString());

    if (prevError) throw prevError;

    const previousRevenue =
      previousDonations?.reduce(
        (sum, donation: any) => sum + (donation.amount_cents || 0),
        0,
      ) / 100 || 0;

    // Calculate percentage changes
    const revenueChange =
      previousRevenue > 0
        ? ((monthlyRevenue - previousRevenue) / previousRevenue) * 100
        : 0;

    // Get donor count from previous period
    const { count: previousDonorCount, error: prevDonorsError } = await supabase
      .from("donors")
      .select("*", { count: "exact", head: true })
      .eq("status", "active")
      .is("deleted_at", null)
      .lte("created_at", thirtyDaysAgo.toISOString());

    if (prevDonorsError) throw prevDonorsError;

    const donorsChange =
      previousDonorCount && previousDonorCount > 0
        ? (((totalDonors || 0) - previousDonorCount) / previousDonorCount) * 100
        : 0;

    return {
      totalClients: totalClients || 0,
      activeCampaigns: activeCampaigns || 0,
      totalDonors: totalDonors || 0,
      monthlyRevenue: Math.round(monthlyRevenue),
      avgGiftSize: Math.round(avgGiftSize),
      donorsChange: Math.round(donorsChange * 10) / 10, // Round to 1 decimal
      revenueChange: Math.round(revenueChange * 10) / 10,
    };
  } catch (error) {
    console.error("Error fetching platform metrics:", error);
    // Return default values on error
    return {
      totalClients: 0,
      activeCampaigns: 0,
      totalDonors: 0,
      monthlyRevenue: 0,
      avgGiftSize: 0,
      donorsChange: 0,
      revenueChange: 0,
    };
  }
}

/**
 * Fetch client-specific metrics for the client dashboard
 */
export async function getClientMetrics(
  clientId: string,
): Promise<ClientMetrics> {
  try {
    // Get total raised amount — PAGINATED: no date filter = whole donation
    // history, which on a large file (Dallas LIFE ≈ 61k) far exceeds the
    // ~1000-row default cap and would corrupt the lifetime total.
    // Bounded with .lte(now): "total raised" means money raised THROUGH TODAY,
    // so future-dated gifts (which exist in the data) are excluded — also keeps
    // the in-memory 30/60-day recent-activity filters below from counting them.
    const nowIso = new Date().toISOString();
    const donations =
      (await collectAllRows(async (from, to) => {
        const { data, error } = await supabase
          .from("donations")
          .select("amount_cents, date")
          .eq("client_id", clientId)
          .lte("date", nowIso)
          .order("id", { ascending: true })
          .range(from, to);
        if (error) throw error;
        return data ?? [];
      })) ?? [];

    const totalRaised =
      donations.reduce(
        (sum, donation: any) => sum + (donation.amount_cents || 0),
        0,
      ) / 100 || 0;

    // Get active campaigns count
    const { count: activeCampaigns, error: campaignsError } = await supabase
      .from("campaigns")
      .select("*", { count: "exact", head: true })
      .eq("client_id", clientId)
      .eq("status", "active")
      .is("deleted_at", null);

    if (campaignsError) throw campaignsError;

    // Get total donors count
    const { count: totalDonors, error: donorsError } = await supabase
      .from("donors")
      .select("*", { count: "exact", head: true })
      .eq("client_id", clientId)
      .eq("status", "active")
      .is("deleted_at", null);

    if (donorsError) throw donorsError;

    // Calculate average donation size
    const avgDonationSize =
      donations && donations.length > 0 ? totalRaised / donations.length : 0;

    // Get recent donations (last 30 days)
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const recentDonations =
      donations?.filter((d: any) => new Date(d.date) >= thirtyDaysAgo) || [];
    const recentDonationCount = recentDonations.length;

    // Calculate donation growth (compare to previous 30 days)
    const sixtyDaysAgo = new Date();
    sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60);

    const previousPeriodDonations =
      donations?.filter(
        (d: any) =>
          new Date(d.date) >= sixtyDaysAgo && new Date(d.date) < thirtyDaysAgo,
      ) || [];

    const donationGrowth =
      previousPeriodDonations.length > 0
        ? ((recentDonationCount - previousPeriodDonations.length) /
            previousPeriodDonations.length) *
          100
        : 0;

    // Get behavioral events for conversion rate calculation
    const { data: events, error: eventsError } = await supabase
      .from("behavioral_events")
      .select("event_type")
      .eq("client_id", clientId)
      .gte("occurred_at", thirtyDaysAgo.toISOString());

    if (eventsError) throw eventsError;

    const totalEvents = events?.length || 0;
    const conversionRate =
      totalEvents > 0 ? (recentDonationCount / totalEvents) * 100 : 0;

    // Recent repeat activity (30d vs prior 30d) — momentum, not annual retention
    const recentDonorIds = new Set(recentDonations.map((d: any) => d.donor_id));
    const previousDonorIds = new Set(
      previousPeriodDonations.map((d: any) => d.donor_id),
    );

    const retainedDonors = Array.from(recentDonorIds).filter((id) =>
      previousDonorIds.has(id),
    ).length;

    const recentRepeatActivityRate =
      previousDonorIds.size > 0
        ? (retainedDonors / previousDonorIds.size) * 100
        : 0;

    return {
      totalRaised: Math.round(totalRaised),
      activeCampaigns: activeCampaigns || 0,
      totalDonors: totalDonors || 0,
      conversionRate: Math.round(conversionRate * 10) / 10,
      avgDonationSize: Math.round(avgDonationSize),
      recentRepeatActivityRate: Math.round(recentRepeatActivityRate * 10) / 10,
      recentDonationCount,
      donationGrowth: Math.round(donationGrowth * 10) / 10,
    };
  } catch (error) {
    console.error("Error fetching client metrics:", error);
    // Return default values on error
    return {
      totalRaised: 0,
      activeCampaigns: 0,
      totalDonors: 0,
      conversionRate: 0,
      avgDonationSize: 0,
      recentRepeatActivityRate: 0,
      recentDonationCount: 0,
      donationGrowth: 0,
    };
  }
}

/**
 * Fetch recent activity for the dashboard feed
 */
export async function getRecentActivity(
  clientId?: string,
  limit: number = 10,
): Promise<ActivityItem[]> {
  try {
    const activities: ActivityItem[] = [];

    // Get recent donations
    const donationsQuery = supabase
      .from("donations")
      .select(
        `
        id,
        amount_cents,
        created_at,
        client_id,
        donors (id, first_name, last_name)
      `,
      )
      .order("created_at", { ascending: false })
      .limit(limit);

    if (clientId) {
      donationsQuery.eq("client_id", clientId);
    }

    const { data: donations, error: donationsError } = await donationsQuery;

    if (!donationsError && donations) {
      donations.forEach((donation: any) => {
        const donor = donation.donors as any;
        const donorName = donor?.id
          ? getDonorDisplayName({
              id: donor.id as string,
              first_name: donor.first_name as string | null,
              last_name: donor.last_name as string | null,
            }) || "Anonymous"
          : "Anonymous";

        activities.push({
          id: donation.id,
          type: "donation",
          message: `New donation of $${(donation.amount_cents / 100).toFixed(0)} from ${donorName}`,
          time: formatTimeAgo(new Date(donation.created_at)),
          clientId: donation.client_id,
        });
      });
    }

    // Get recent campaign updates
    const campaignsQuery = supabase
      .from("campaigns")
      .select(
        "id, name, created_at, client_id, status, raised_amount, goal_amount",
      )
      .order("created_at", { ascending: false })
      .limit(limit);

    if (clientId) {
      campaignsQuery.eq("client_id", clientId);
    }

    const { data: campaigns, error: campaignsError } = await campaignsQuery;

    if (!campaignsError && campaigns) {
      campaigns.forEach((campaign: any) => {
        const progress = campaign.goal_amount
          ? Math.round(
              ((campaign.raised_amount || 0) / campaign.goal_amount) * 100,
            )
          : 0;

        activities.push({
          id: campaign.id,
          type: "campaign",
          message: `${campaign.name} ${campaign.status === "active" ? `reached ${progress}% of goal` : `is now ${campaign.status}`}`,
          time: formatTimeAgo(new Date(campaign.created_at)),
          clientId: campaign.client_id,
          entityId: campaign.id,
        });
      });
    }

    // Sort all activities by time and return top N
    activities.sort(
      (a, b) => parseTimeAgo(b.time).getTime() - parseTimeAgo(a.time).getTime(),
    );

    return activities.slice(0, limit);
  } catch (error) {
    console.error("Error fetching recent activity:", error);
    return [];
  }
}

/**
 * Format a date as relative time (e.g., "2 hours ago")
 */
function formatTimeAgo(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return "just now";
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? "" : "s"} ago`;
  if (diffHour < 24) return `${diffHour} hour${diffHour === 1 ? "" : "s"} ago`;
  if (diffDay < 7) return `${diffDay} day${diffDay === 1 ? "" : "s"} ago`;
  return date.toLocaleDateString();
}

/**
 * Parse a relative time string back to a Date (approximate)
 */
function parseTimeAgo(timeStr: string): Date {
  const now = new Date();

  if (timeStr === "just now") return now;

  const match = timeStr.match(/(\d+)\s+(minute|hour|day)s?\s+ago/);
  if (match) {
    const value = parseInt(match[1], 10);
    const unit = match[2];

    switch (unit) {
      case "minute":
        return new Date(now.getTime() - value * 60 * 1000);
      case "hour":
        return new Date(now.getTime() - value * 60 * 60 * 1000);
      case "day":
        return new Date(now.getTime() - value * 24 * 60 * 60 * 1000);
    }
  }

  return now;
}

// ============================================================================
// TRACK15-SPECIFIC ANALYTICS
// ============================================================================

/**
 * Donor segments for Track15 methodology
 */
export type DonorSegment =
  | "current"
  | "lapsed"
  | "high_value"
  | "prospects"
  | "monthly";

/**
 * Track15 segment performance metrics
 */
export interface SegmentPerformance {
  segment: DonorSegment;
  donorCount: number;
  totalRaised: number;
  avgGiftSize: number;
  retentionRate: number;
  growth: number; // percentage change
}

/**
 * Track15 retention metrics
 */
export interface RetentionMetrics {
  annualDonorRetentionRate: number; // canonical cohort retention, latest complete calendar year
  annualDonorRetentionRateAvailable: boolean; // false = insufficient history (not 0%)
  renewalRate: number; // % of last year's donors who renewed (tracks annual retention)
  lapsedReactivationRate: number; // % of lapsed donors reactivated
  newDonorRetention: number; // % of new donors who gave again
  averageGiftFrequency: number; // avg gifts per donor per year
}

/**
 * Track15 year-over-year comparison
 */
export interface YearOverYearMetrics {
  currentYearRevenue: number;
  previousYearRevenue: number;
  percentageChange: number;
  currentYearDonors: number;
  previousYearDonors: number;
  donorGrowth: number;
  avgGiftChange: number;
}

/**
 * Campaign lift metrics (week-over-week)
 */
export interface CampaignLiftMetrics {
  campaignId: string;
  campaignName: string;
  weeklyLift: Array<{
    week: string;
    revenue: number;
    donorCount: number;
    liftPercentage: number;
  }>;
  totalLift: number;
}

/**
 * Channel attribution data
 */
export interface ChannelAttribution {
  channel: string;
  touchpoints: number;
  conversions: number;
  revenue: number;
  averageGiftSize: number;
  conversionRate: number;
}

/**
 * Pure: share of total lifetime giving held by the top decile of donors.
 * Top decile = top `ceil(n * 0.1)` donors by amount (at least 1). Defensive:
 * non-finite/negative amounts are treated as 0. Returns 0..1; 0 for empty/zero total.
 * Canonical home for the desk's concentration metric (no fork — compute it ONLY here).
 */
export function computeTopDecileShare(totals: number[]): number {
  const clean = totals.map((t) => (Number.isFinite(t) && t > 0 ? t : 0));
  const total = clean.reduce((a, b) => a + b, 0);
  if (total <= 0) return 0;
  const sorted = clean.slice().sort((a, b) => b - a);
  const topCount = Math.max(1, Math.ceil(sorted.length * 0.1));
  const topSum = sorted.slice(0, topCount).reduce((a, b) => a + b, 0);
  return topSum / total;
}

/** Top-donor concentration for one client (history-derived; never throws). */
export interface DonorConcentration {
  available: boolean;
  topDecileShare?: number; // 0..1
}

/** Default PostgREST page window — Supabase caps a single select (~1000 rows). */
export const ROW_PAGE_SIZE = 1000;
/**
 * Safety ceiling for a full table sweep. Realistic nonprofit files are well under
 * this; beyond it we return null rather than silently process a partial set —
 * "no silent caps". A 15k-donor file (Dallas LIFE) reads in ~16 pages.
 */
export const ROW_SWEEP_MAX_ROWS = 200_000;

/**
 * Pure: accumulate every row by paging through `fetchPage(from, to)` until a short
 * page signals exhaustion. Returns null if the ceiling is hit WITHOUT exhausting —
 * the caller must then treat the result as incomplete (e.g. unavailable), never
 * compute over a partial sweep. A single Supabase select is capped (~1000 rows),
 * so any per-client donor/donation aggregate that must be COMPLETE goes through
 * this. Testable without a Supabase mock.
 */
export async function collectAllRows<T>(
  fetchPage: (from: number, to: number) => Promise<T[]>,
  pageSize: number = ROW_PAGE_SIZE,
  maxRows: number = ROW_SWEEP_MAX_ROWS,
): Promise<T[] | null> {
  const rows: T[] = [];
  for (let from = 0; from < maxRows; from += pageSize) {
    const batch = await fetchPage(from, from + pageSize - 1);
    rows.push(...batch);
    if (batch.length < pageSize) return rows; // exhausted — complete sweep
  }
  return null; // ceiling hit without exhausting — caller must not use a partial set
}

/**
 * Fetch EVERY donor's lifetime giving (paginated — a single select is capped at
 * ~1000 rows, which on a 15k-donor file would silently measure only the first
 * window) and compute the top-decile share. available:false when too few donors
 * (< 10), an oversized file (> ceiling), or the fetch fails — absence is never
 * underperformance. One column, client-scoped; no segments-table dependency.
 */
export async function getDonorConcentration(
  clientId: string,
): Promise<DonorConcentration> {
  try {
    // Concentration is a distribution over LICENSED giving. Reading
    // `donors.total_donated` with `|| 0` entered every unestablished donor as a
    // $0 data point, which drags the decile share toward concentration that was
    // never measured.
    const totals = await collectAllRows<number>(async (from, to) => {
      const { data, error } = await supabase
        .from("donor_metric_authority" as never)
        .select("observed_giving_total")
        .eq("client_id", clientId)
        .order("donor_id", { ascending: true }) // stable order so pages don't overlap/skip
        .range(from, to);
      if (error) throw error;
      return (
        (data ?? []) as unknown as { observed_giving_total: number | null }[]
      )
        .map((d) => d.observed_giving_total)
        .filter((v): v is number => v !== null);
    });
    if (!totals || totals.length < 10) return { available: false };
    return { available: true, topDecileShare: computeTopDecileShare(totals) };
  } catch {
    return { available: false };
  }
}

/**
 * Get segment performance for a client
 *
 * SEGMENT PRECEDENCE (evaluated in order):
 * 1. monthly    - Has recurring donations (is_recurring=true) in period
 * 2. high_value - Lifetime giving >= $1000
 * 3. prospects  - Exactly 1 lifetime gift
 * 4. lapsed     - No gift in last `periodDays` AND has previous gifts
 * 5. current    - Active donor with gifts in period
 *
 * Note: A donor can only be in ONE segment per this classification.
 * Note: total_donated is stored in DOLLARS (NUMERIC 12,2), not cents.
 */
export async function getSegmentPerformance(
  clientId: string,
  periodDays: number = 365,
): Promise<SegmentPerformance[]> {
  try {
    const periodStart = new Date();
    periodStart.setDate(periodStart.getDate() - periodDays);
    const periodStartISO = periodStart.toISOString();

    // 1. Get donors with aggregate metrics — PAGINATED to sweep the FULL file.
    // A single select caps at ~1000 rows; on a 15k-donor file that would
    // truncate segment counts (e.g. lapsed) to the first window.
    // NOTE: We include all statuses to count lapsed donors correctly
    // (lapsed donors might still be status='active' in many systems)
    const donors = await collectAllRows(async (from, to) => {
      const { data, error } = await supabase
        .from("donors")
        .select("id, status")
        .eq("client_id", clientId)
        .is("deleted_at", null)
        .order("id", { ascending: true }) // stable order so pages don't overlap/skip
        .range(from, to);
      if (error) throw error;
      return data ?? [];
    });

    if (!donors || donors.length === 0) return [];

    // Governed metrics from the authority, joined by donor_id. `status` stays
    // on the donor record; giving, counts and gift dates do not.
    const authorityRows = await collectAllRows(async (from, to) => {
      const { data, error } = await supabase
        .from("donor_metric_authority" as never)
        .select("donor_id, observed_giving_total, gift_count, last_gift_date")
        .eq("client_id", clientId)
        .order("donor_id", { ascending: true })
        .range(from, to);
      if (error) throw error;
      return data ?? [];
    });

    const metricsByDonor = new Map(
      (
        authorityRows as unknown as {
          donor_id: string;
          observed_giving_total: number | null;
          gift_count: number | null;
          last_gift_date: string | null;
        }[]
      ).map((a) => [a.donor_id, a]),
    );

    // 2. Get donations in period for revenue calculations — PAGINATED (a busy
    // file can exceed one window of in-period gifts, truncating period stats).
    const donations =
      (await collectAllRows(async (from, to) => {
        const { data, error } = await supabase
          .from("donations")
          .select("donor_id, amount_cents, is_recurring")
          .eq("client_id", clientId)
          .gte("date", periodStartISO)
          .lte("date", new Date().toISOString()) // through today — a future-dated gift must NOT mark a donor as having an in-period gift (would wrongly exclude them from 'lapsed')
          .order("id", { ascending: true })
          .range(from, to);
        if (error) throw error;
        return data ?? [];
      })) ?? [];

    // 3. Build donation lookup: donor_id -> { giftCount, totalCents, hasRecurring }
    const donationStats = new Map<
      string,
      {
        giftCount: number;
        totalCents: number;
        hasRecurring: boolean;
      }
    >();

    donations?.forEach((d: any) => {
      const curr = donationStats.get(d.donor_id) || {
        giftCount: 0,
        totalCents: 0,
        hasRecurring: false,
      };
      donationStats.set(d.donor_id, {
        giftCount: curr.giftCount + 1,
        totalCents: curr.totalCents + (d.amount_cents || 0),
        hasRecurring: curr.hasRecurring || d.is_recurring === true,
      });
    });

    // 4. Classify each donor into a segment
    // Segment precedence: monthly > high_value > prospects > lapsed > current
    interface DonorWithSegment {
      id: string;
      total_donated: number | null;
      donation_count: number | null;
      last_donation_date: string | null;
      status: string | null;
      segment: DonorSegment;
      periodStats: { giftCount: number; totalCents: number } | null;
    }

    const classifiedDonors: DonorWithSegment[] = donors.map((donor: any) => {
      const stats = donationStats.get(donor.id);
      const metrics = metricsByDonor.get(donor.id);
      const lastGiftDate = metrics?.last_gift_date
        ? new Date(metrics.last_gift_date)
        : null;

      // ⚠️ NULL, NOT 0. `high_value` below is a SEGMENT ASSIGNMENT — a claim
      // about the donor. With `|| 0` a donor whose giving is unestablished was
      // reported as under the threshold and silently segmented elsewhere.
      const lifetimeDollars = metrics?.observed_giving_total ?? null;
      const lifetimeGiftCount = metrics?.gift_count ?? null;
      const hasGiftInPeriod = stats && stats.giftCount > 0;
      const hasRecurring = stats?.hasRecurring || false;

      // Check lapsed: no gift in period but has historical gifts
      const isLapsed =
        !hasGiftInPeriod &&
        lifetimeGiftCount !== null &&
        lifetimeGiftCount > 0 &&
        lastGiftDate &&
        lastGiftDate < periodStart;

      // Determine segment (precedence order)
      let segment: DonorSegment;
      if (hasRecurring) {
        segment = "monthly";
      } else if (lifetimeDollars !== null && lifetimeDollars >= 1000) {
        segment = "high_value";
      } else if (lifetimeGiftCount === 1) {
        segment = "prospects";
      } else if (isLapsed) {
        segment = "lapsed";
      } else if (hasGiftInPeriod) {
        segment = "current";
      } else {
        // No gifts ever or edge case - treat as prospects
        segment = "prospects";
      }

      return {
        ...donor,
        segment,
        periodStats: stats
          ? {
              giftCount: stats.giftCount,
              totalCents: stats.totalCents,
            }
          : null,
      };
    });

    // 5. Group by segment and calculate metrics
    const segmentGroups = new Map<DonorSegment, DonorWithSegment[]>();
    const allSegments: DonorSegment[] = [
      "current",
      "lapsed",
      "high_value",
      "prospects",
      "monthly",
    ];
    allSegments.forEach((s) => segmentGroups.set(s, []));

    classifiedDonors.forEach((d) => {
      segmentGroups.get(d.segment)!.push(d);
    });

    // 6. Calculate performance metrics per segment
    const performance: SegmentPerformance[] = allSegments.map((segment) => {
      const segmentDonors = segmentGroups.get(segment) || [];
      const donorCount = segmentDonors.length;

      // Sum period revenue (in cents, convert to dollars)
      const totalCents = segmentDonors.reduce(
        (sum, d) => sum + (d.periodStats?.totalCents || 0),
        0,
      );
      const totalRaised = totalCents / 100;

      // Sum gift count for proper avg gift calculation
      const totalGifts = segmentDonors.reduce(
        (sum, d) => sum + (d.periodStats?.giftCount || 0),
        0,
      );

      // avgGiftSize = total raised / number of gifts (not donors)
      const avgGiftSize = totalGifts > 0 ? totalRaised / totalGifts : 0;

      // Retention rate: donors with recent activity (last 90 days)
      const ninetyDaysAgo = new Date();
      ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
      const recentDonors = segmentDonors.filter((d) => {
        const lastGift = d.last_donation_date
          ? new Date(d.last_donation_date)
          : null;
        return lastGift && lastGift >= ninetyDaysAgo;
      });
      const retentionRate =
        donorCount > 0 ? (recentDonors.length / donorCount) * 100 : 0;

      return {
        segment,
        donorCount,
        totalRaised: Math.round(totalRaised),
        avgGiftSize: Math.round(avgGiftSize),
        retentionRate: Math.round(retentionRate * 10) / 10,
        growth: 0, // Would need previous period comparison - omit for now
      };
    });

    return performance;
  } catch (error) {
    console.error("Error fetching segment performance:", error);
    return [];
  }
}

/**
 * Get retention metrics for a client
 */
export async function getRetentionMetrics(
  clientId: string,
): Promise<RetentionMetrics> {
  try {
    const now = new Date();
    const oneYearAgo = new Date(
      now.getFullYear() - 1,
      now.getMonth(),
      now.getDate(),
    );
    const twoYearsAgo = new Date(
      now.getFullYear() - 2,
      now.getMonth(),
      now.getDate(),
    );

    // Get donations for current and previous year — PAGINATED: a 2-year window
    // on an active file (≈10k gifts/yr) exceeds the ~1000-row cap; truncation
    // corrupts both numerator and denominator of the retention rate.
    const donations =
      (await collectAllRows(async (from, to) => {
        const { data, error } = await supabase
          .from("donations")
          .select("donor_id, date, amount_cents")
          .eq("client_id", clientId)
          .gte("date", twoYearsAgo.toISOString())
          .lte("date", now.toISOString()) // through today — exclude future-dated gifts from the donor split
          .order("id", { ascending: true })
          .range(from, to);
        if (error) throw error;
        return data ?? [];
      })) ?? [];

    // Canonical annual retention needs FULL calendar years [P-1, P]; the
    // trailing-window query above misses early-year gifts. Fetch a clean
    // 3-year window by Jan 1 so the prior-year denominator is complete.
    // PAGINATED for the same reason — a multi-year sweep far exceeds one window.
    const retentionFromDate = new Date(
      Date.UTC(now.getUTCFullYear() - 2, 0, 1),
    ).toISOString();
    const retentionRows =
      (await collectAllRows(async (from, to) => {
        const { data, error } = await supabase
          .from("donations")
          .select("donor_id, date, amount_cents")
          .eq("client_id", clientId)
          .gte("date", retentionFromDate)
          .lte("date", now.toISOString()) // through today (computeAnnualRetention bounds by complete year anyway, but keep it consistent)
          .order("id", { ascending: true })
          .range(from, to);
        if (error) throw error;
        return data ?? [];
      })) ?? [];

    const { rate: annualRate, available: annualAvailable } =
      computeAnnualRetention(retentionRows as RetentionDonationRow[], now);

    const annualDonorRetentionRate = Math.round(annualRate * 10) / 10;
    const renewalRate = annualDonorRetentionRate; // tracks annual retention

    // Segment donations by period
    const currentYearDonors = new Set<string>();
    const previousYearDonors = new Set<string>();
    const newDonorGifts = new Map<string, number>();

    donations?.forEach((donation: any) => {
      const date = new Date(donation.date);
      const donorId = donation.donor_id;

      if (date >= oneYearAgo) {
        currentYearDonors.add(donorId);
        // Track repeat gifts for new donors
        newDonorGifts.set(donorId, (newDonorGifts.get(donorId) || 0) + 1);
      } else {
        previousYearDonors.add(donorId);
      }
    });

    // Calculate retention metrics
    const retainedDonors = Array.from(currentYearDonors).filter((id) =>
      previousYearDonors.has(id),
    );

    // New donor retention (donors who gave multiple times this year)
    const newDonors = Array.from(currentYearDonors).filter(
      (id) => !previousYearDonors.has(id),
    );
    const repeatNewDonors = newDonors.filter(
      (id) => (newDonorGifts.get(id) || 0) > 1,
    );
    const newDonorRetention =
      newDonors.length > 0
        ? (repeatNewDonors.length / newDonors.length) * 100
        : 0;

    // Lapsed reactivation (donors who gave 2+ years ago but not last year, now giving again)
    const lapsedReactivated = retainedDonors.filter((id) => {
      const lastYearGifts = donations?.filter(
        (d: any) =>
          d.donor_id === id &&
          new Date(d.date) >= oneYearAgo &&
          new Date(d.date) < now,
      );
      return lastYearGifts && lastYearGifts.length === 0;
    });

    const lapsedReactivationRate =
      previousYearDonors.size > 0
        ? (lapsedReactivated.length / previousYearDonors.size) * 100
        : 0;

    // Average gift frequency
    const totalDonations = donations?.length || 0;
    const totalUniqueDonors = new Set(donations?.map((d: any) => d.donor_id))
      .size;
    const averageGiftFrequency =
      totalUniqueDonors > 0 ? totalDonations / totalUniqueDonors : 0;

    return {
      annualDonorRetentionRate,
      annualDonorRetentionRateAvailable: annualAvailable,
      renewalRate: Math.round(renewalRate * 10) / 10,
      lapsedReactivationRate: Math.round(lapsedReactivationRate * 10) / 10,
      newDonorRetention: Math.round(newDonorRetention * 10) / 10,
      averageGiftFrequency: Math.round(averageGiftFrequency * 10) / 10,
    };
  } catch (error) {
    console.error("Error fetching retention metrics:", error);
    return {
      annualDonorRetentionRate: 0,
      annualDonorRetentionRateAvailable: false,
      renewalRate: 0,
      lapsedReactivationRate: 0,
      newDonorRetention: 0,
      averageGiftFrequency: 0,
    };
  }
}

/**
 * Get year-over-year comparison metrics
 */
export async function getYearOverYearMetrics(
  clientId: string,
): Promise<YearOverYearMetrics> {
  try {
    const now = new Date();
    const currentYearStart = new Date(now.getFullYear(), 0, 1);
    const previousYearStart = new Date(now.getFullYear() - 1, 0, 1);
    const previousYearEnd = new Date(now.getFullYear() - 1, 11, 31);

    // Get current year data — PAGINATED: a full year on an active file exceeds
    // the ~1000-row cap and would corrupt the revenue/donor totals below.
    const currentDonations =
      (await collectAllRows(async (from, to) => {
        const { data, error } = await supabase
          .from("donations")
          .select("amount_cents, donor_id")
          .eq("client_id", clientId)
          .gte("date", currentYearStart.toISOString())
          .lte("date", now.toISOString()) // current year THROUGH TODAY — exclude future-dated gifts
          .order("id", { ascending: true })
          .range(from, to);
        if (error) throw error;
        return data ?? [];
      })) ?? [];

    // Get previous year data — PAGINATED (full prior year, same risk).
    const previousDonations =
      (await collectAllRows(async (from, to) => {
        const { data, error } = await supabase
          .from("donations")
          .select("amount_cents, donor_id")
          .eq("client_id", clientId)
          .gte("date", previousYearStart.toISOString())
          .lte("date", previousYearEnd.toISOString())
          .order("id", { ascending: true })
          .range(from, to);
        if (error) throw error;
        return data ?? [];
      })) ?? [];

    // Calculate totals
    const currentYearRevenue =
      currentDonations?.reduce(
        (sum, d: any) => sum + (d.amount_cents || 0),
        0,
      ) / 100 || 0;

    const previousYearRevenue =
      previousDonations?.reduce(
        (sum, d: any) => sum + (d.amount_cents || 0),
        0,
      ) / 100 || 0;

    const percentageChange =
      previousYearRevenue > 0
        ? ((currentYearRevenue - previousYearRevenue) / previousYearRevenue) *
          100
        : 0;

    // Calculate unique donors
    const currentYearDonors = new Set(
      currentDonations?.map((d: any) => d.donor_id),
    ).size;
    const previousYearDonors = new Set(
      previousDonations?.map((d: any) => d.donor_id),
    ).size;

    const donorGrowth =
      previousYearDonors > 0
        ? ((currentYearDonors - previousYearDonors) / previousYearDonors) * 100
        : 0;

    // Calculate average gift change
    const currentAvgGift =
      currentDonations && currentDonations.length > 0
        ? currentYearRevenue / currentDonations.length
        : 0;
    const previousAvgGift =
      previousDonations && previousDonations.length > 0
        ? previousYearRevenue / previousDonations.length
        : 0;

    const avgGiftChange =
      previousAvgGift > 0
        ? ((currentAvgGift - previousAvgGift) / previousAvgGift) * 100
        : 0;

    return {
      currentYearRevenue: Math.round(currentYearRevenue),
      previousYearRevenue: Math.round(previousYearRevenue),
      percentageChange: Math.round(percentageChange * 10) / 10,
      currentYearDonors,
      previousYearDonors,
      donorGrowth: Math.round(donorGrowth * 10) / 10,
      avgGiftChange: Math.round(avgGiftChange * 10) / 10,
    };
  } catch (error) {
    console.error("Error fetching year-over-year metrics:", error);
    return {
      currentYearRevenue: 0,
      previousYearRevenue: 0,
      percentageChange: 0,
      currentYearDonors: 0,
      previousYearDonors: 0,
      donorGrowth: 0,
      avgGiftChange: 0,
    };
  }
}

/**
 * Get campaign lift metrics (weekly performance tracking)
 */
export async function getCampaignLift(
  campaignId: string,
): Promise<CampaignLiftMetrics | null> {
  try {
    // Get campaign details
    const { data: campaign, error: campaignError } = await supabase
      .from("campaigns")
      .select("id, name, start_date, end_date, client_id")
      .eq("id", campaignId)
      .single();

    if (campaignError || !campaign) throw campaignError;

    const startDate = campaign.start_date
      ? new Date(campaign.start_date)
      : new Date();
    const endDate = campaign.end_date
      ? new Date(campaign.end_date)
      : new Date();

    // Get donations during campaign period — PAGINATED: a long/major campaign
    // window can exceed the ~1000-row cap and truncate the weekly lift rollup.
    const donations =
      (await collectAllRows(async (from, to) => {
        const { data, error } = await supabase
          .from("donations")
          .select("amount_cents, date, donor_id")
          .eq("client_id", campaign.client_id)
          .gte("date", startDate.toISOString())
          .lte("date", endDate.toISOString())
          .order("id", { ascending: true })
          .range(from, to);
        if (error) throw error;
        return data ?? [];
      })) ?? [];

    // Group by week
    const weeklyData = new Map<
      string,
      { revenue: number; donors: Set<string> }
    >();

    donations?.forEach((donation: any) => {
      const date = new Date(donation.date);
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay()); // Start of week (Sunday)
      const weekKey = weekStart.toISOString().split("T")[0];

      if (!weeklyData.has(weekKey)) {
        weeklyData.set(weekKey, { revenue: 0, donors: new Set() });
      }

      const weekData = weeklyData.get(weekKey)!;
      weekData.revenue += (donation.amount_cents || 0) / 100;
      weekData.donors.add(donation.donor_id);
    });

    // Calculate lift week-over-week
    const weeks = Array.from(weeklyData.entries()).sort((a, b) =>
      a[0].localeCompare(b[0]),
    );

    const weeklyLift = weeks.map((entry, index) => {
      const [week, data] = entry;
      const previousWeek = index > 0 ? weeks[index - 1][1] : null;

      const liftPercentage = previousWeek
        ? previousWeek.revenue > 0
          ? ((data.revenue - previousWeek.revenue) / previousWeek.revenue) * 100
          : 0
        : 0;

      return {
        week,
        revenue: Math.round(data.revenue),
        donorCount: data.donors.size,
        liftPercentage: Math.round(liftPercentage * 10) / 10,
      };
    });

    // Calculate total lift (first week to last week)
    const totalLift =
      weeks.length > 1 && weeks[0][1].revenue > 0
        ? ((weeks[weeks.length - 1][1].revenue - weeks[0][1].revenue) /
            weeks[0][1].revenue) *
          100
        : 0;

    return {
      campaignId: campaign.id,
      campaignName: campaign.name,
      weeklyLift,
      totalLift: Math.round(totalLift * 10) / 10,
    };
  } catch (error) {
    console.error("Error fetching campaign lift:", error);
    return null;
  }
}

// ============================================================================
// MONTH-OVER-MONTH ANALYTICS
// ============================================================================

/**
 * Month-over-month comparison metrics
 */
export interface MonthOverMonthMetrics {
  currentMonthRevenue: number;
  previousMonthRevenue: number;
  revenueChange: number;
  currentMonthDonors: number;
  previousMonthDonors: number;
  donorChange: number;
  currentMonthGiftCount: number;
  previousMonthGiftCount: number;
  giftCountChange: number;
  avgGiftCurrent: number;
  avgGiftPrevious: number;
  avgGiftChange: number;
}

/**
 * Rolling period comparison (flexible window)
 */
export interface RollingComparisonMetrics {
  periodDays: number;
  currentPeriod: {
    startDate: string;
    endDate: string;
    revenue: number;
    donorCount: number;
    giftCount: number;
    avgGift: number;
  };
  previousPeriod: {
    startDate: string;
    endDate: string;
    revenue: number;
    donorCount: number;
    giftCount: number;
    avgGift: number;
  };
  changes: {
    revenue: number;
    donors: number;
    gifts: number;
    avgGift: number;
  };
}

/**
 * Trend data point for time series
 */
export interface TrendDataPoint {
  period: string;
  revenue: number;
  donorCount: number;
  giftCount: number;
  avgGift: number;
}

/**
 * Get month-over-month comparison metrics
 */
export async function getMonthOverMonthMetrics(
  clientId: string,
): Promise<MonthOverMonthMetrics> {
  try {
    const now = new Date();
    const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const previousMonthStart = new Date(
      now.getFullYear(),
      now.getMonth() - 1,
      1,
    );
    const previousMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);

    // Get current month donations. Bounded with .lte(now): the open-ended .gte
    // would otherwise sweep future-dated gifts (which exist in the data) past the
    // current month. A single bounded month stays under the ~1000-row cap.
    const { data: currentDonations, error: currentError } = await supabase
      .from("donations")
      .select("amount_cents, donor_id")
      .eq("client_id", clientId)
      .gte("date", currentMonthStart.toISOString())
      .lte("date", now.toISOString());

    if (currentError) throw currentError;

    // Get previous month donations
    const { data: previousDonations, error: previousError } = await supabase
      .from("donations")
      .select("amount_cents, donor_id")
      .eq("client_id", clientId)
      .gte("date", previousMonthStart.toISOString())
      .lte("date", previousMonthEnd.toISOString());

    if (previousError) throw previousError;

    // Calculate current month metrics
    const currentMonthRevenue =
      currentDonations?.reduce(
        (sum, d: any) => sum + (d.amount_cents || 0),
        0,
      ) / 100 || 0;
    const currentMonthDonors = new Set(
      currentDonations?.map((d: any) => d.donor_id),
    ).size;
    const currentMonthGiftCount = currentDonations?.length || 0;
    const avgGiftCurrent =
      currentMonthGiftCount > 0
        ? currentMonthRevenue / currentMonthGiftCount
        : 0;

    // Calculate previous month metrics
    const previousMonthRevenue =
      previousDonations?.reduce(
        (sum, d: any) => sum + (d.amount_cents || 0),
        0,
      ) / 100 || 0;
    const previousMonthDonors = new Set(
      previousDonations?.map((d: any) => d.donor_id),
    ).size;
    const previousMonthGiftCount = previousDonations?.length || 0;
    const avgGiftPrevious =
      previousMonthGiftCount > 0
        ? previousMonthRevenue / previousMonthGiftCount
        : 0;

    // Calculate percentage changes
    const revenueChange =
      previousMonthRevenue > 0
        ? ((currentMonthRevenue - previousMonthRevenue) /
            previousMonthRevenue) *
          100
        : 0;
    const donorChange =
      previousMonthDonors > 0
        ? ((currentMonthDonors - previousMonthDonors) / previousMonthDonors) *
          100
        : 0;
    const giftCountChange =
      previousMonthGiftCount > 0
        ? ((currentMonthGiftCount - previousMonthGiftCount) /
            previousMonthGiftCount) *
          100
        : 0;
    const avgGiftChange =
      avgGiftPrevious > 0
        ? ((avgGiftCurrent - avgGiftPrevious) / avgGiftPrevious) * 100
        : 0;

    return {
      currentMonthRevenue: Math.round(currentMonthRevenue),
      previousMonthRevenue: Math.round(previousMonthRevenue),
      revenueChange: Math.round(revenueChange * 10) / 10,
      currentMonthDonors,
      previousMonthDonors,
      donorChange: Math.round(donorChange * 10) / 10,
      currentMonthGiftCount,
      previousMonthGiftCount,
      giftCountChange: Math.round(giftCountChange * 10) / 10,
      avgGiftCurrent: Math.round(avgGiftCurrent),
      avgGiftPrevious: Math.round(avgGiftPrevious),
      avgGiftChange: Math.round(avgGiftChange * 10) / 10,
    };
  } catch (error) {
    console.error("Error fetching month-over-month metrics:", error);
    return {
      currentMonthRevenue: 0,
      previousMonthRevenue: 0,
      revenueChange: 0,
      currentMonthDonors: 0,
      previousMonthDonors: 0,
      donorChange: 0,
      currentMonthGiftCount: 0,
      previousMonthGiftCount: 0,
      giftCountChange: 0,
      avgGiftCurrent: 0,
      avgGiftPrevious: 0,
      avgGiftChange: 0,
    };
  }
}

/**
 * Get rolling period comparison (flexible days window)
 */
export async function getRollingComparison(
  clientId: string,
  periodDays: number = 30,
): Promise<RollingComparisonMetrics> {
  try {
    const now = new Date();
    const currentPeriodStart = new Date(
      now.getTime() - periodDays * 24 * 60 * 60 * 1000,
    );
    const previousPeriodStart = new Date(
      currentPeriodStart.getTime() - periodDays * 24 * 60 * 60 * 1000,
    );
    const previousPeriodEnd = new Date(currentPeriodStart.getTime() - 1);

    // Get current period donations. Bounded with .lte(now) so the open-ended
    // .gte doesn't sweep future-dated gifts past the window. The sole caller
    // uses a 30-day window (well under the ~1000-row cap); a caller passing a
    // much larger periodDays should switch this to collectAllRows pagination.
    const { data: currentDonations, error: currentError } = await supabase
      .from("donations")
      .select("amount_cents, donor_id")
      .eq("client_id", clientId)
      .gte("date", currentPeriodStart.toISOString())
      .lte("date", now.toISOString());

    if (currentError) throw currentError;

    // Get previous period donations
    const { data: previousDonations, error: previousError } = await supabase
      .from("donations")
      .select("amount_cents, donor_id")
      .eq("client_id", clientId)
      .gte("date", previousPeriodStart.toISOString())
      .lte("date", previousPeriodEnd.toISOString());

    if (previousError) throw previousError;

    // Calculate current period metrics
    const currentRevenue =
      currentDonations?.reduce(
        (sum, d: any) => sum + (d.amount_cents || 0),
        0,
      ) / 100 || 0;
    const currentDonorCount = new Set(
      currentDonations?.map((d: any) => d.donor_id),
    ).size;
    const currentGiftCount = currentDonations?.length || 0;
    const currentAvgGift =
      currentGiftCount > 0 ? currentRevenue / currentGiftCount : 0;

    // Calculate previous period metrics
    const previousRevenue =
      previousDonations?.reduce(
        (sum, d: any) => sum + (d.amount_cents || 0),
        0,
      ) / 100 || 0;
    const previousDonorCount = new Set(
      previousDonations?.map((d: any) => d.donor_id),
    ).size;
    const previousGiftCount = previousDonations?.length || 0;
    const previousAvgGift =
      previousGiftCount > 0 ? previousRevenue / previousGiftCount : 0;

    // Calculate percentage changes
    const revenueChange =
      previousRevenue > 0
        ? ((currentRevenue - previousRevenue) / previousRevenue) * 100
        : 0;
    const donorChange =
      previousDonorCount > 0
        ? ((currentDonorCount - previousDonorCount) / previousDonorCount) * 100
        : 0;
    const giftChange =
      previousGiftCount > 0
        ? ((currentGiftCount - previousGiftCount) / previousGiftCount) * 100
        : 0;
    const avgGiftChange =
      previousAvgGift > 0
        ? ((currentAvgGift - previousAvgGift) / previousAvgGift) * 100
        : 0;

    return {
      periodDays,
      currentPeriod: {
        startDate: currentPeriodStart.toISOString().split("T")[0],
        endDate: now.toISOString().split("T")[0],
        revenue: Math.round(currentRevenue),
        donorCount: currentDonorCount,
        giftCount: currentGiftCount,
        avgGift: Math.round(currentAvgGift),
      },
      previousPeriod: {
        startDate: previousPeriodStart.toISOString().split("T")[0],
        endDate: previousPeriodEnd.toISOString().split("T")[0],
        revenue: Math.round(previousRevenue),
        donorCount: previousDonorCount,
        giftCount: previousGiftCount,
        avgGift: Math.round(previousAvgGift),
      },
      changes: {
        revenue: Math.round(revenueChange * 10) / 10,
        donors: Math.round(donorChange * 10) / 10,
        gifts: Math.round(giftChange * 10) / 10,
        avgGift: Math.round(avgGiftChange * 10) / 10,
      },
    };
  } catch (error) {
    console.error("Error fetching rolling comparison:", error);
    return {
      periodDays,
      currentPeriod: {
        startDate: "",
        endDate: "",
        revenue: 0,
        donorCount: 0,
        giftCount: 0,
        avgGift: 0,
      },
      previousPeriod: {
        startDate: "",
        endDate: "",
        revenue: 0,
        donorCount: 0,
        giftCount: 0,
        avgGift: 0,
      },
      changes: { revenue: 0, donors: 0, gifts: 0, avgGift: 0 },
    };
  }
}

/**
 * Get monthly trend data for time series charts
 */
export async function getMonthlyTrend(
  clientId: string,
  months: number = 12,
): Promise<TrendDataPoint[]> {
  try {
    const now = new Date();
    const startDate = new Date(
      now.getFullYear(),
      now.getMonth() - months + 1,
      1,
    );

    // PAGINATED: the default 12-month window on an active file (≈10k gifts/yr)
    // exceeds the ~1000-row cap and would corrupt the per-month rollup.
    const donations =
      (await collectAllRows(async (from, to) => {
        const { data, error } = await supabase
          .from("donations")
          .select("amount_cents, donor_id, date")
          .eq("client_id", clientId)
          .gte("date", startDate.toISOString())
          .lte("date", now.toISOString()) // trailing window THROUGH TODAY — exclude future-dated gifts
          .order("id", { ascending: true })
          .range(from, to);
        if (error) throw error;
        return data ?? [];
      })) ?? [];

    // Group by month
    const monthlyData = new Map<
      string,
      { revenue: number; donors: Set<string>; gifts: number }
    >();

    // Initialize all months
    for (let i = 0; i < months; i++) {
      const monthDate = new Date(
        now.getFullYear(),
        now.getMonth() - months + 1 + i,
        1,
      );
      const key = `${monthDate.getFullYear()}-${String(monthDate.getMonth() + 1).padStart(2, "0")}`;
      monthlyData.set(key, { revenue: 0, donors: new Set(), gifts: 0 });
    }

    // Aggregate donations
    donations?.forEach((d: any) => {
      const date = new Date(d.date);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      if (monthlyData.has(key)) {
        const data = monthlyData.get(key)!;
        data.revenue += (d.amount_cents || 0) / 100;
        data.donors.add(d.donor_id);
        data.gifts++;
      }
    });

    // Convert to array
    return Array.from(monthlyData.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([period, data]) => ({
        period,
        revenue: Math.round(data.revenue),
        donorCount: data.donors.size,
        giftCount: data.gifts,
        avgGift: data.gifts > 0 ? Math.round(data.revenue / data.gifts) : 0,
      }));
  } catch (error) {
    console.error("Error fetching monthly trend:", error);
    return [];
  }
}

/**
 * Get channel attribution data
 */
export async function getChannelAttribution(
  clientId: string,
  periodDays: number = 90,
): Promise<ChannelAttribution[]> {
  try {
    const periodStart = new Date();
    periodStart.setDate(periodStart.getDate() - periodDays);

    // Get behavioral events (touchpoints)
    const { data: events, error: eventsError } = await supabase
      .from("behavioral_events")
      .select("event_type, metadata, donor_id, occurred_at")
      .eq("client_id", clientId)
      .gte("occurred_at", periodStart.toISOString());

    if (eventsError) throw eventsError;

    // Get donations for conversion tracking — PAGINATED + bounded .lte(now):
    // a 90-day window on a large file can exceed the ~1000-row cap, and the
    // open-ended .gte would otherwise leak future-dated gifts into the channel
    // conversion/revenue rollup.
    const donations =
      (await collectAllRows(async (from, to) => {
        const { data, error } = await supabase
          .from("donations")
          .select("donor_id, amount_cents, date, metadata")
          .eq("client_id", clientId)
          .gte("date", periodStart.toISOString())
          .lte("date", new Date().toISOString())
          .order("id", { ascending: true })
          .range(from, to);
        if (error) throw error;
        return data ?? [];
      })) ?? [];

    // Map channels from events
    const channelMap = new Map<
      string,
      { touchpoints: number; conversions: number; revenue: number }
    >();

    // Count touchpoints by channel
    events?.forEach((event: any) => {
      const channel = event.metadata?.channel || "direct";
      if (!channelMap.has(channel)) {
        channelMap.set(channel, { touchpoints: 0, conversions: 0, revenue: 0 });
      }
      channelMap.get(channel)!.touchpoints++;
    });

    // Count conversions and revenue by channel
    donations?.forEach((donation: any) => {
      const channel = donation.metadata?.channel || "direct";
      if (!channelMap.has(channel)) {
        channelMap.set(channel, { touchpoints: 0, conversions: 0, revenue: 0 });
      }
      const data = channelMap.get(channel)!;
      data.conversions++;
      data.revenue += (donation.amount_cents || 0) / 100;
    });

    // Build attribution array
    const attribution: ChannelAttribution[] = Array.from(
      channelMap.entries(),
    ).map(([channel, data]) => ({
      channel,
      touchpoints: data.touchpoints,
      conversions: data.conversions,
      revenue: Math.round(data.revenue),
      averageGiftSize:
        data.conversions > 0 ? Math.round(data.revenue / data.conversions) : 0,
      conversionRate:
        data.touchpoints > 0
          ? Math.round((data.conversions / data.touchpoints) * 100 * 10) / 10
          : 0,
    }));

    return attribution.sort((a, b) => b.revenue - a.revenue);
  } catch (error) {
    console.error("Error fetching channel attribution:", error);
    return [];
  }
}
